import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './UnifiedModal.css';

const MODAL_SIZES = {
  sm: 'unified-modal-sm',      // 400px max-width
  md: 'unified-modal-md',      // 600px max-width  
  lg: 'unified-modal-lg',      // 900px max-width
  xl: 'unified-modal-xl',      // 1200px max-width
  full: 'unified-modal-full',   // 90vw max-width (for diagram modals)
  custom: 'unified-modal-custom' // No size constraints
};

const UnifiedModal = ({
  isOpen,
  onClose,
  size = 'md',
  children,
  className = '',
  overlayClassName = '',
  closeOnOverlayClick = true,
  showCloseButton = true,
  preventBodyScroll = true,
  zIndex = 1050,
  ariaLabel = 'Modal dialog',
  ...props
}) => {
  // Escape key handling
  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Body scroll prevention and event handling
  useEffect(() => {
    if (!isOpen) return;

    // Prevent body scroll
    if (preventBodyScroll) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      // Cleanup function
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen, preventBodyScroll]);

  // Escape key handling
  useEffect(() => {
    if (!isOpen) return;

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, handleEscapeKey]);

  // Don't render if not open
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div 
      className={`unified-modal-overlay ${overlayClassName}`}
      onClick={handleOverlayClick}
      style={{ zIndex }}
      data-testid="unified-modal-overlay"
    >
      <div 
        className={`unified-modal-container ${MODAL_SIZES[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-testid="unified-modal-container"
        {...props}
      >
        {showCloseButton && (
          <button
            type="button"
            className="unified-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            data-testid="unified-modal-close"
          >
            <span aria-hidden="true">&times;</span>
            <span className="unified-modal-close-sr">Close</span>
          </button>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default UnifiedModal;
